import { PostgresCollectionConfig } from "@rebasepro/types";

const usersCollection: PostgresCollectionConfig = {
    name: "Usuarios",
    singularName: "Usuario",
    slug: "users",
    auth: true,
    table: "users",
    schema: "rebase",
    securityRules: [
        {
            operation: "select",
            roles: ["admin"]
        },
        {
            operations: ["insert", "update", "delete"],
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid",
            admin: { readOnly: true }
        },
        email: {
            name: "Email",
            type: "string",
            validation: {
                required: true,
                unique: true
            }
        },
        displayName: {
            name: "Nombre",
            type: "string",
            columnName: "display_name",
            validation: { required: true }
        },
        photoURL: {
            name: "Foto",
            type: "string",
            columnName: "photo_url",
            admin: {
                urlPreview: "image"
            }
        },
        roles: {
            name: "Roles",
            type: "array",
            columnType: "text[]",
            of: {
                name: "Rol",
                type: "string",
                enum: {
                    admin: "Administrador",
                    editor: "Editor",
                    viewer: "Solo lectura"
                }
            }
        },
        passwordHash: {
            name: "Contraseña (cifrada, no se puede leer)",
            type: "string",
            columnName: "password_hash",
            // `hideFromCollection` hides it from this panel and serves it to
            // every caller the select policy admits — which was happening: the
            // hash was in every row the users list fetched. Only this flag
            // takes the column off the wire. It is also what the create form
            // reads to know the column is not its to send: without it the form
            // submitted `passwordHash: null` and the server refused the write.
            excludeFromApi: true,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        emailVerified: {
            name: "Email verificado",
            type: "boolean",
            columnName: "email_verified",
            defaultValue: false,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        emailVerificationToken: {
            name: "Código para verificar el correo",
            type: "string",
            columnName: "email_verification_token",
            // A pending token is a free password reset. See passwordHash.
            excludeFromApi: true,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        emailVerificationSentAt: {
            name: "Verificación enviada el",
            type: "date",
            columnName: "email_verification_sent_at",
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        metadata: {
            name: "Datos internos de la cuenta",
            type: "map",
            keyValue: true,
            properties: {},
            defaultValue: {},
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        createdAt: {
            name: "Creado",
            type: "date",
            columnName: "created_at",
            autoValue: "on_create",
            admin: { readOnly: true }
        },
        updatedAt: {
            name: "Actualizado",
            type: "date",
            columnName: "updated_at",
            autoValue: "on_update",
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        }
    },
    admin: {
        // Not "Users": the collapsed drawer showed this and "Candidatos" as
        // the same two silhouettes, and an operator could not tell the
        // accounts screen from the people screen.
        icon: "UserCog",
        group: "Ajustes",
        openEntityMode: "dialog",
        disableDefaultActions: ["copy"],
        // Whoever can use the panel first, then everyone else, each group
        // newest first. There are five accounts with a role and three hundred
        // without, so ordering by `createdAt` alone put the operators behind
        // six pages of candidates who had signed up to be looked at. `roles` is
        // a `text[]` and Postgres compares arrays element by element, so the
        // empty one sorts lowest and `desc` floats every account that has a
        // role. Within that group the array itself orders before the date, so
        // an editor sits above an administrador — five rows, and the operators
        // are together at the top, which is the whole of what this is for.
        sort: [["roles", "desc"], ["createdAt", "desc"]],
        // The derived title was the email, with the person's name demoted to a
        // subtitle and the email printed a second time as a column — so every
        // row led with the least readable of the two things it knows and then
        // repeated it. Naming the roles puts the person first.
        display: {
            title: "displayName",
            subtitle: "email",
            date: "createdAt"
        },
        // `email` is the subtitle now; `displayName` is the title.
        listProperties: ["roles", "createdAt"],
        // The fallback would take the first three of `propertiesOrder` and add
        // `roles` — an array of chips that says nothing about *which* person
        // this is. A preview only has to identify the row.
        previewProperties: ["displayName", "email"],
        propertiesOrder: ["id", "email", "displayName", "roles", "createdAt"]
    }
};

export default usersCollection;
